import { useMemo, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import type {
    Member,
    MemberProfessional,
    Professional,
} from 'generated/graphql';
import type { MeUser } from '~/server/types';
import type { UserActiveStatus } from '~/modules/user';
import { getUserInitials, useUserActiveStatus } from '~/modules/user';
import { StatusIndicator } from './StatusIndicator';

type MeType = Pick<
    MeUser,
    '__typename' | 'ID' | 'Name' | 'Initials' | 'ProfileImage'
>;
type UserType = Pick<
    Member,
    '__typename' | 'ID' | 'Name' | 'Initials' | 'ProfileImageLink'
>;
type ProfessionalType = Pick<
    Professional,
    '__typename' | 'ID' | 'Title' | 'CompanyLogo' | 'LogoBackgroundColor'
>;
type MemberProfessionalType = Pick<
    MemberProfessional,
    '__typename' | 'ID' | 'Title' | 'CompanyLogo' | 'LogoBackgroundColor'
>;
type Participant =
    | MeType
    | UserType
    | ProfessionalType
    | MemberProfessionalType;

/**
 * The avatar component for a conversation participant. The participant can be
 * a user (member) or a professional.
 */
export function ParticipantAvatar({
    className,
    imageClassName,
    fallbackClassName,
    participant,
    activeStatus,
    showUserStatus = false,
    singleLetterFallback = false,
    ...props
}: {
    className?: string;
    imageClassName?: string;
    fallbackClassName?: string;
    participant?: Participant;
    /**
     * If set, shows a status indicator dot next to the avatar. This overrides
     * the status from the `showUserStatus` prop.
     */
    activeStatus?: UserActiveStatus;
    /**
     * Show the user's active status. Only works for member participants. This
     * prop is ignored if `activeStatus` is set. Defaults to `false`.
     */
    showUserStatus?: boolean;
    singleLetterFallback?: boolean;
} & React.ComponentPropsWithoutRef<typeof Avatar>) {
    const [failedImageSrc, setFailedImageSrc] = useState<string | null>(null);
    const isProfessional =
        participant?.__typename === 'Professional' ||
        participant?.__typename === 'MemberProfessional';
    const isMember = participant?.__typename === 'Member';
    const memberID =
        !activeStatus && showUserStatus && isMember
            ? participant.ID
            : undefined;
    const userStatuses = useUserActiveStatus(memberID ? [memberID] : []);
    const userStatus = memberID ? userStatuses[memberID] : undefined;
    const realActiveStatus = activeStatus ?? userStatus;
    const imageSrc = isProfessional
        ? participant.CompanyLogo
        : isMember
          ? participant.ProfileImageLink
          : participant?.ProfileImage;
    const fallback = useMemo(() => {
        const initials = isProfessional
            ? participant.Title[0]?.toUpperCase()
            : participant
              ? getUserInitials(participant)
              : '';
        return singleLetterFallback ? initials?.[0] : initials;
    }, [isProfessional, participant, singleLetterFallback]);

    return (
        <Avatar
            className={cn(
                'transition-[width,height] duration-200 ease-linear will-change-[width,height]',
                className,
            )}
            {...props}
        >
            <AvatarImage
                // Add white background for images with transparent background.
                className={cn('bg-white', imageClassName)}
                src={imageSrc || undefined}
                alt={isProfessional ? participant.Title : participant?.Name}
                fit={isProfessional ? 'contain' : 'cover'}
                style={
                    isProfessional
                        ? {
                              // Professionals without a logo background color defaults to white.
                              backgroundColor:
                                  participant.LogoBackgroundColor ?? 'white',
                          }
                        : undefined
                }
                onError={() => setFailedImageSrc(imageSrc || null)}
            />
            {/* Show fallback conditionally to prevent flashing fallback when image changes. */}
            {(!imageSrc || failedImageSrc === imageSrc) && (
                <AvatarFallback
                    className={cn(
                        'transition-[font-size] duration-200 ease-linear will-change-[font-size]',
                        fallbackClassName,
                    )}
                    delayMs={0}
                >
                    {fallback}
                </AvatarFallback>
            )}
            {realActiveStatus && <StatusIndicator status={realActiveStatus} />}
        </Avatar>
    );
}
